import { useEffect, useState } from 'react';
import { useCustomAuth } from 'auth/AuthContext';
import { Box, Collapse, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
    ArrowForwardIosRounded,
    ArrowBackIosRounded,
    AutoGraph,
    ContactPageOutlined,
    ExpandLess,
    ExpandMore,
    PaidOutlined,
    SettingsOutlined,
    SupervisedUserCircleOutlined,
    SwapCalls,
} from '@mui/icons-material';
import Button from 'domains/core/components/Button';
import ContentIcon from '../SvgIcons/ContentIcon';
import NavigationButton, { NavigationButtonVariant } from './NavigationButton';
import { URLPaths } from 'models/enums';
import CuredLogoWithText from 'assets/images/svg/cured-logo-green.svg';
import CuredLogoNoText from 'assets/images/svg/cured-logo-green-no-text.svg';
import { maximizeLeftNavPanel, minimizeLeftNavPanel } from 'state/appVisual/appVisualActions';
import { Link } from 'react-router-dom';
import ProfileButton from './ProfileButton';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { shouldShowSettingsNavForFlags } from './shouldShowSettingsNavForFlags';

const useStyles = makeStyles((theme: Theme) => ({
    toggleNavIcon: {
        color: 'white',
        fontSize: '1rem',
    },
}));

export const NavigationPanel = () => {
    const dispatch = useDispatch();
    const isLeftNavPanelMaximized = useSelector((state: any) => state.appVisual.isLeftNavPanelMaximized);

    const { isAuthenticated, user } = useCustomAuth();
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings({
        enabled: isAuthenticated,
    });
    const {
        accessControlLayer: accessControlLayerFlag,
        contentOnly: hasContentOnlyFlag,
        accessCenter: hasAccessCenterFlag,
        aiBrandTools: hasAiBrandToolsFlag,
        customerSupport: hasCustomerSupportFlag,
    } = useFlags();

    const {
        canViewAccessManagement,
        canViewContent,
        canAccessContacts,
        canViewAudiences,
        canViewCurations,
        canViewValue,
        canViewInsightsDashboards,
        isAccessCentrePermissionsLoading,
    } = useAccessCentrePermissions();

    const accessCentreNavReady = !isAccessCentrePermissionsLoading;

    const isFlagReady =
        isFeatureSettingsSuccess &&
        hasContentOnlyFlag !== undefined &&
        accessControlLayerFlag !== undefined &&
        hasAccessCenterFlag !== undefined;

    /**
     * Legacy (Access Centre off): show item. When AC is on, wait for `isFlagReady` and /me so we never
     * flash "full" nav — `hasAccessCenterFlag !== true` was true while LD was still undefined.
     */
    const showNavIfPermitted = (can: boolean) =>
        isFlagReady && (hasAccessCenterFlag !== true ? true : accessCentreNavReady && can);

    const showSettingsNav =
        isFlagReady && shouldShowSettingsNavForFlags(hasAccessCenterFlag, hasAiBrandToolsFlag, hasCustomerSupportFlag);

    /** Users / Roles links are Access Centre–only; Brand setup stays under AI brand flag inside the submenu. */
    const showUsersAndRolesInSettingsNav =
        hasAccessCenterFlag === true && accessCentreNavReady && canViewAccessManagement;

    const showContentNavItem = showNavIfPermitted(canViewContent);
    const showCurationsNavItem = showNavIfPermitted(canViewCurations);
    const showAudiencesNavItem = showNavIfPermitted(canViewAudiences);
    const showContactsNavItem = showNavIfPermitted(canAccessContacts);
    const showValueNavItem = showNavIfPermitted(canViewValue);
    const showInsightsNavItem = showNavIfPermitted(canViewInsightsDashboards);

    const hasAccessControlLayer = featureSettings?.acl_enabled && accessControlLayerFlag;
    const hasContentOnly = featureSettings?.content_only_mode_enabled && hasContentOnlyFlag;

    const history = useHistory();
    const location = useLocation();
    const classes = useStyles();
    const [isHover, setIsHover] = useState(false);
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const isSettingsPath = location.pathname.startsWith('/settings');

    useEffect(() => {
        if (isSettingsPath) setSettingsExpanded(true);
    }, [isSettingsPath]);

    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            {/* Overlay of main page body when nav panel is expanded */}
            <Box
                hidden={!isLeftNavPanelMaximized}
                bgcolor="#95a6a733"
                position="fixed"
                left={0}
                top={0}
                width="100%"
                height="100%"
                zIndex={10}
                onClick={() => dispatch(minimizeLeftNavPanel(false))}
            />
            <Box hidden={!isLeftNavPanelMaximized}>
                {/* navigation panel when maximized */}
                <Box
                    height="100%"
                    width="15.25rem"
                    sx={{
                        backgroundColor: 'secondary.light',
                        borderLeftColor: 'secondary.light',
                        borderLeftStyle: 'solid',
                        borderLeftWidth: '0.5rem',
                    }}
                    position="fixed"
                    left={0}
                    top={0}
                    zIndex={11}
                    overflow="hidden"
                    flex={1}
                >
                    <List dense disablePadding>
                        <ProfileButton userEmail={user.email} />
                        <Box
                            display="flex"
                            flexDirection="row"
                            marginTop={2}
                            marginLeft={2}
                            marginRight={2}
                            marginBottom={10}
                        >
                            <Link to="/">
                                <img
                                    src={CuredLogoWithText}
                                    alt="Cured logo"
                                    height="32"
                                    width="100%"
                                    onClick={() => dispatch(minimizeLeftNavPanel(false))}
                                />
                            </Link>
                        </Box>
                        {!hasContentOnly && showCurationsNavItem && (
                            <NavigationButton
                                title="curations"
                                path={URLPaths.CURATIONS}
                                helperText="Setup, test, and schedule interactions."
                                icon={<SwapCalls />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showAudiencesNavItem && (
                            <NavigationButton
                                title="audiences"
                                path={URLPaths.SEGMENTS}
                                helperText="Filter contacts to create new audiences."
                                icon={<SupervisedUserCircleOutlined />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showContactsNavItem && (
                            <NavigationButton
                                title="contacts"
                                path={URLPaths.CONTACTS}
                                helperText="Browse existing contacts and upload new ones."
                                icon={<ContactPageOutlined />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {showContentNavItem && (
                            <NavigationButton
                                title="content"
                                path={URLPaths.CONTENT}
                                helperText="Create your own content or customize a template to fit your brand."
                                icon={<ContentIcon />}
                                variant={NavigationButtonVariant.WIDE}
                                dataTestId="content-button-list-item"
                                ariaLabel="Open content"
                            />
                        )}
                        {!hasContentOnly && showValueNavItem && (
                            <NavigationButton
                                title="value"
                                path={URLPaths.VALUE}
                                helperText="Track the impact of your marketing efforts."
                                icon={<PaidOutlined />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {!hasContentOnly && showInsightsNavItem && (
                            <NavigationButton
                                title="insights"
                                path={URLPaths.INSIGHTS}
                                helperText="Monitor the performance of Curations and gather insights on contacts."
                                icon={<AutoGraph />}
                                variant={NavigationButtonVariant.WIDE}
                            />
                        )}
                        {showSettingsNav && (
                            <>
                                <ListItem
                                    button
                                    onClick={() => setSettingsExpanded(!settingsExpanded)}
                                    sx={{
                                        color: isSettingsPath ? 'secondary.main' : 'primary.main',
                                        height: '3.25rem',
                                        '&:hover': { color: 'action.active' },
                                    }}
                                    data-testid="access-center-nav-settings"
                                    role="button"
                                    aria-expanded={settingsExpanded}
                                    aria-haspopup="true"
                                >
                                    <ListItemIcon sx={{ color: 'inherit' }}>
                                        <SettingsOutlined />
                                    </ListItemIcon>
                                    <ListItemText
                                        sx={{ whiteSpace: 'nowrap' }}
                                        disableTypography
                                        primary={
                                            <Typography color="inherit" variant="body1" mb={0.75}>
                                                settings
                                            </Typography>
                                        }
                                    />
                                    {settingsExpanded ? (
                                        <ExpandLess sx={{ color: 'inherit' }} />
                                    ) : (
                                        <ExpandMore sx={{ color: 'inherit' }} />
                                    )}
                                </ListItem>
                                <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
                                    <List
                                        component="div"
                                        disablePadding
                                        dense
                                        sx={{ pl: 6 }}
                                        data-testid="access-center-nav-settings-submenu"
                                        role="list"
                                    >
                                        {showUsersAndRolesInSettingsNav && (
                                            <>
                                                <ListItem
                                                    button
                                                    onClick={() => {
                                                        dispatch(minimizeLeftNavPanel(false));
                                                        history.push(URLPaths.SETTINGS_USERS);
                                                    }}
                                                    selected={location.pathname === URLPaths.SETTINGS_USERS}
                                                    sx={{
                                                        color:
                                                            location.pathname === URLPaths.SETTINGS_USERS
                                                                ? 'secondary.main'
                                                                : 'primary.main',
                                                        height: '3.25rem',
                                                        '&:hover': { color: 'action.active' },
                                                    }}
                                                    data-testid="access-center-nav-users"
                                                    role="menuitem"
                                                >
                                                    <ListItemText
                                                        disableTypography
                                                        primary={
                                                            <Typography color="inherit" variant="body1" mb={0.75}>
                                                                users
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                                <ListItem
                                                    button
                                                    onClick={() => {
                                                        dispatch(minimizeLeftNavPanel(false));
                                                        history.push(URLPaths.SETTINGS_ROLES);
                                                    }}
                                                    selected={location.pathname === URLPaths.SETTINGS_ROLES}
                                                    sx={{
                                                        color:
                                                            location.pathname === URLPaths.SETTINGS_ROLES
                                                                ? 'secondary.main'
                                                                : 'primary.main',
                                                        height: '3.25rem',
                                                        '&:hover': { color: 'action.active' },
                                                    }}
                                                    data-testid="access-center-nav-roles"
                                                    role="menuitem"
                                                >
                                                    <ListItemText
                                                        disableTypography
                                                        primary={
                                                            <Typography color="inherit" variant="body1" mb={0.75}>
                                                                roles
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            </>
                                        )}
                                        {hasCustomerSupportFlag && (
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    dispatch(minimizeLeftNavPanel(false));
                                                    history.push(URLPaths.SETTINGS_CUSTOMER_SUPPORT);
                                                }}
                                                selected={location.pathname === URLPaths.SETTINGS_CUSTOMER_SUPPORT}
                                                sx={{
                                                    color:
                                                        location.pathname === URLPaths.SETTINGS_CUSTOMER_SUPPORT
                                                            ? 'secondary.main'
                                                            : 'primary.main',
                                                    height: '3.25rem',
                                                    '&:hover': { color: 'action.active' },
                                                }}
                                                data-testid="access-center-nav-customer-support"
                                                role="menuitem"
                                            >
                                                <ListItemText
                                                    disableTypography
                                                    primary={
                                                        <Typography color="inherit" variant="body1" mb={0.75}>
                                                            customer support
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        )}
                                        {hasAiBrandToolsFlag && (
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    dispatch(minimizeLeftNavPanel(false));
                                                    history.push(URLPaths.SETTINGS_BRAND);
                                                }}
                                                selected={
                                                    location.pathname === URLPaths.SETTINGS_BRAND ||
                                                    location.pathname === URLPaths.SETTINGS_BRAND_EDIT
                                                }
                                                sx={{
                                                    color:
                                                        location.pathname === URLPaths.SETTINGS_BRAND ||
                                                        location.pathname === URLPaths.SETTINGS_BRAND_EDIT
                                                            ? 'secondary.main'
                                                            : 'primary.main',
                                                    height: '3.25rem',
                                                    '&:hover': { color: 'action.active' },
                                                }}
                                                data-testid="access-center-nav-brand-setup"
                                                role="menuitem"
                                            >
                                                <ListItemText
                                                    disableTypography
                                                    primary={
                                                        <Typography color="inherit" variant="body1" mb={0.75}>
                                                            brand setup
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                </Collapse>
                            </>
                        )}
                    </List>
                </Box>
                {/* navigation panel border when maximized */}
                <Box
                    height="100%"
                    width="0.25rem"
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        transitionDuration: '0.23s',
                        transitionProperty: 'background-color',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={() => dispatch(minimizeLeftNavPanel(false))}
                    position="fixed"
                    left="15.75rem"
                    top={0}
                    zIndex={4}
                />
                {/* navigation panel toggle when maximized */}
                <Button
                    onClick={() => {
                        dispatch(minimizeLeftNavPanel(false));
                        setIsHover(false);
                    }}
                    onMouseOver={() => {
                        setIsHover(true);
                    }}
                    onMouseLeave={() => {
                        setIsHover(false);
                    }}
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        minWidth: '2rem',
                        maxWidth: '2rem',
                        minHeight: '2rem',
                        maxHeight: '2rem',
                        padding: '0rem',
                        borderRadius: '0.25rem 0 0 0.25rem',
                        position: 'fixed',
                        bottom: '4rem',
                        left: '13.75rem',
                        zIndex: 5,
                        '&:hover': {
                            backgroundColor: 'secondary.containedHoverBackground',
                        },
                    }}
                >
                    <ArrowBackIosRounded classes={{ root: classes.toggleNavIcon }} />
                </Button>
            </Box>
            {/* navigation panel when minimized */}
            <Box hidden={isLeftNavPanelMaximized}>
                <Box
                    height="100%"
                    width="4rem"
                    sx={{ backgroundColor: 'secondary.light' }}
                    position="fixed"
                    left={0}
                    top={0}
                    zIndex={5}
                    overflow="hidden"
                    flex={1}
                >
                    <List dense disablePadding>
                        <ProfileButton userEmail={user.email} />
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                            height="100%"
                            marginTop={2}
                            marginBottom={10}
                        >
                            <Link to="/">
                                <img
                                    src={CuredLogoNoText}
                                    alt="Cured logo"
                                    width="32"
                                    height="32"
                                    onClick={() => dispatch(minimizeLeftNavPanel(false))}
                                />
                            </Link>
                        </Box>
                        {!hasContentOnly && showCurationsNavItem && (
                            <NavigationButton
                                path={URLPaths.CURATIONS}
                                helperText="curations"
                                icon={<SwapCalls />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showAudiencesNavItem && (
                            <NavigationButton
                                path={URLPaths.SEGMENTS}
                                helperText="audiences"
                                icon={<SupervisedUserCircleOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {!hasAccessControlLayer && !hasContentOnly && showContactsNavItem && (
                            <NavigationButton
                                path={URLPaths.CONTACTS}
                                helperText="contacts"
                                icon={<ContactPageOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {showContentNavItem && (
                            <NavigationButton
                                path={URLPaths.CONTENT}
                                helperText="content"
                                icon={<ContentIcon />}
                                variant={NavigationButtonVariant.NARROW}
                                dataTestId="content-button-list-item"
                                ariaLabel="Open content"
                            />
                        )}
                        {!hasContentOnly && showValueNavItem && (
                            <NavigationButton
                                path={URLPaths.VALUE}
                                helperText="value"
                                icon={<PaidOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {!hasContentOnly && showInsightsNavItem && (
                            <NavigationButton
                                path={URLPaths.INSIGHTS}
                                helperText="insights"
                                icon={<AutoGraph />}
                                variant={NavigationButtonVariant.NARROW}
                            />
                        )}
                        {showSettingsNav && (
                            <NavigationButton
                                path="/settings"
                                helperText="settings"
                                icon={<SettingsOutlined />}
                                variant={NavigationButtonVariant.NARROW}
                                onClickOverride={() => {
                                    dispatch(maximizeLeftNavPanel());
                                    setSettingsExpanded(true);
                                }}
                                dataTestId="access-center-nav-settings"
                                ariaLabel="Open settings menu"
                            />
                        )}
                    </List>
                </Box>
                {/* navigation panel border when minimized */}
                <Box
                    height="100%"
                    width="0.25rem"
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        transitionDuration: '0.23s',
                        transitionProperty: 'background-color',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={() => dispatch(maximizeLeftNavPanel())}
                    position="fixed"
                    left="4rem"
                    top={0}
                    zIndex={4}
                />
                {/* navigation panel toggle when minimized */}
                <Button
                    onClick={() => {
                        dispatch(maximizeLeftNavPanel());
                        setIsHover(false);
                    }}
                    onMouseOver={() => {
                        setIsHover(true);
                    }}
                    onMouseLeave={() => {
                        setIsHover(false);
                    }}
                    sx={{
                        backgroundColor: isHover ? 'secondary.containedHoverBackground' : 'secondary.main',
                        minWidth: '2rem',
                        maxWidth: '2rem',
                        minHeight: '2rem',
                        maxHeight: '2rem',
                        padding: '0rem',
                        borderRadius: '0 0.25rem 0.25rem 0',
                        position: 'fixed',
                        bottom: '4rem',
                        left: '4rem',
                        zIndex: 5,
                        '&:hover': {
                            backgroundColor: 'secondary.containedHoverBackground',
                        },
                    }}
                >
                    <ArrowForwardIosRounded classes={{ root: classes.toggleNavIcon }} />
                </Button>
            </Box>
        </>
    );
};

export default NavigationPanel;
